/**
 * Utility functions for managing likes and dislikes in meal planning
 */

import { Profile, profileApi } from "../services/api";

const MAX_MEAL_NAME_LENGTH = 50;

/**
 * Intelligently parse a likes/dislikes string into individual items
 */
export const parseLikesDislikesString = (
    input: string | undefined | null
): string[] => {
    if (!input || typeof input !== "string") {
        return [];
    }

    // First split by newlines and remove blanks
    let items = input
        .split("\n")
        .map((item) => item.trim())
        .filter((item) => item.length > 0);

    // For items longer than max meal name length that contain commas, split by commas
    const processedItems: string[] = [];
    items.forEach((item) => {
        if (item.length > MAX_MEAL_NAME_LENGTH && item.includes(",")) {
            // Split by commas and add each part
            const commaItems = item
                .split(",")
                .map((subItem) => subItem.trim())
                .filter((subItem) => subItem.length > 0);
            processedItems.push(...commaItems);
        } else {
            processedItems.push(item);
        }
    });

    // Remove duplicates (case insensitive) and return
    const seen = new Set<string>();
    return processedItems.filter((item) => {
        const lower = item.toLowerCase();
        if (seen.has(lower)) {
            return false;
        }
        seen.add(lower);
        return true;
    });
};

/**
 * Check if a meal name appears in a list of items (case insensitive)
 */
export const isMealInList = (
    mealName: string,
    itemsList: string[]
): boolean => {
    if (!mealName || !Array.isArray(itemsList)) {
        return false;
    }

    const mealNameLower = mealName.toLowerCase();
    return itemsList.some((item) => item.toLowerCase() === mealNameLower);
};

/**
 * Add a meal name to a likes/dislikes string
 */
export const addMealToList = (
    currentList: string | undefined | null,
    mealName: string
): string => {
    if (!mealName) {
        return currentList || "";
    }

    const current = currentList || "";
    const items = parseLikesDislikesString(current);

    // Don't add if already exists (case insensitive)
    if (isMealInList(mealName, items)) {
        return current;
    }

    // Add the new meal name
    return current ? `${current}\n${mealName}` : mealName;
};

/**
 * Remove a meal name from a likes/dislikes string
 */
export const removeMealFromList = (
    currentList: string | undefined | null,
    mealName: string
): string => {
    if (!mealName || !currentList) {
        return currentList || "";
    }

    const items = parseLikesDislikesString(currentList);
    const mealNameLower = mealName.toLowerCase();

    // Filter out the meal name (case insensitive)
    const filteredItems = items.filter(
        (item) => item.toLowerCase() !== mealNameLower
    );

    return filteredItems.join("\n");
};

/**
 * Get the like/dislike state for a meal
 */
export const getMealPreferenceState = (
    mealName: string,
    likes: string | undefined | null,
    dislikes: string | undefined | null
): "liked" | "disliked" | "neutral" => {
    const likesList = parseLikesDislikesString(likes);
    const dislikesList = parseLikesDislikesString(dislikes);

    if (isMealInList(mealName, likesList)) {
        return "liked";
    } else if (isMealInList(mealName, dislikesList)) {
        return "disliked";
    }
    return "neutral";
};

/**
 * Update profile likes/dislikes
 */
export const updateProfilePreferences = async (
    userId: number,
    profileId: number,
    action: "like" | "dislike" | "unlike" | "undislike",
    mealName: string,
    currentProfile: Profile
): Promise<Profile> => {
    const currentLikes = currentProfile.profile_data.likes;
    const currentDislikes = currentProfile.profile_data.dislikes;

    let newLikes = currentLikes;
    let newDislikes = currentDislikes;

    switch (action) {
        case "like":
            // Remove from dislikes if present, add to likes
            newDislikes = removeMealFromList(currentDislikes, mealName);
            newLikes = addMealToList(currentLikes, mealName);
            break;
        case "dislike":
            // Remove from likes if present, add to dislikes
            newLikes = removeMealFromList(currentLikes, mealName);
            newDislikes = addMealToList(currentDislikes, mealName);
            break;
        case "unlike":
            // Remove from likes
            newLikes = removeMealFromList(currentLikes, mealName);
            break;
        case "undislike":
            // Remove from dislikes
            newDislikes = removeMealFromList(currentDislikes, mealName);
            break;
    }

    // Update the profile
    const updatedProfile = await profileApi.updateProfile(userId, profileId, {
        profile_data: {
            ...currentProfile.profile_data,
            likes: newLikes,
            dislikes: newDislikes,
        },
    });

    return updatedProfile;
};
